import React, { Fragment, useState, useEffect } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { SUBSCRIPTION_PLANS } from '../utils/stripe'

const Subscription = () => {
    const { user } = useAuth()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [loading, setLoading] = useState(false)
    const [currentSubscription, setCurrentSubscription] = useState(null)
    // eslint-disable-next-line no-unused-vars
    const [sellerData, setSellerData] = useState(null)

    // Get billing period from URL or default to yearly
    // eslint-disable-next-line no-unused-vars
    const urlPlan = searchParams.get('plan')
    const urlBilling = searchParams.get('billing')
    const [billingPeriod, setBillingPeriod] = useState(urlBilling || 'yearly')

    useEffect(() => {
        if (!user) {
            navigate('/auth/login?redirect=/subscription')
            return
        }
        loadSubscriptionData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user])

    const loadSubscriptionData = async () => {
        if (!user) return

        try {
            // Get seller data
            const { data: seller } = await supabase
                .from('sellers')
                .select('*')
                .eq('user_id', user.id)
                .single()

            setSellerData(seller)

            // Get current subscription if exists
            if (seller) {
                const { data: subscription } = await supabase
                    .from('subscriptions')
                    .select('*')
                    .eq('seller_id', seller.id)
                    .eq('status', 'active')
                    .single()

                setCurrentSubscription(subscription)
            }
        } catch (error) {
            console.error('Error loading subscription:', error)
        }
    }

    const handleSubscribe = async (planTier) => {
        if (!user) {
            navigate('/auth/login?redirect=/subscription')
            return
        }

        setLoading(true)

        try {
            const planConfig = SUBSCRIPTION_PLANS[planTier][billingPeriod]

            // Create Stripe Checkout Session via Edge Function
            const { data, error } = await supabase.functions.invoke('create-checkout-session', {
                body: {
                    priceId: planConfig.priceId,
                    planTier: planTier,
                    billingPeriod: billingPeriod,
                    userId: user.id,
                    userEmail: user.email,
                    successUrl: `${window.location.origin}/subscription?session_id={CHECKOUT_SESSION_ID}`,
                    cancelUrl: `${window.location.origin}/subscription`,
                }
            })

            if (error) {
                throw error
            }

            // Redirect to Stripe Checkout
            if (data?.url) {
                window.location.href = data.url
            }
        } catch (error) {
            console.error('Error creating checkout session:', error)
            alert('Failed to start checkout. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const handleManageSubscription = async () => {
        setLoading(true)

        try {
            // Create Stripe Customer Portal Session
            const { data, error } = await supabase.functions.invoke('create-portal-session', {
                body: {
                    userId: user.id,
                    returnUrl: `${window.location.origin}/subscription`,
                }
            })

            if (error) {
                throw error
            }

            // Redirect to Stripe Customer Portal
            if (data?.url) {
                window.location.href = data.url
            }
        } catch (error) {
            console.error('Error opening billing portal:', error)
            alert('Failed to open billing portal. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    const plans = [
        {
            tier: 'hobbyist',
            name: 'Kinn Hobbyist',
            description: 'Home cooks, side hustlers, first-time sellers',
            features: [
                'Verified seller badge (safety + trust)',
                'Get discovered on Kinn marketplace',
                'Personalised shopfront',
                'Simple order and product management tools',
                'Stripe/Apple/Google Pay integration',
                'In-app chat with buyers',
                'Access to Kinn community tips & updates',
            ],
            popular: false,
        },
        {
            tier: 'pro',
            name: 'Kinn Pro',
            description: 'Licensed cooks, micro-bakers, sellers ready to scale',
            features: [
                'All Hobbyist features, plus:',
                'AI Seller Assistant - Menu writing & auto-listing',
                'Smart Social Generator - Instagram posts & captions',
                'Priority placement in marketplace search',
                'Analytics Dashboard - Orders, top products, buyers',
                'Featured "Pro Kitchen" badge',
            ],
            popular: true,
        },
    ]

    return (
        <Fragment>
            <section className='pb-0 pt-[120px] bg-[#F8F8F8] min-h-screen'>
                <Container>
                    <div className="text-center mb-8">
                        <h2 className='text-h2 text-Mblack mb-3'>Choose Your Plan</h2>
                        <p className='text-body-lg text-gray-600 mb-6'>
                            Select the perfect plan for your business. No commission on sales - just a simple subscription.
                        </p>

                        {/* Billing Period Toggle */}
                        <div className="flex items-center justify-center gap-3 mb-8">
                            <span className={`text-body-md ${billingPeriod === 'monthly' ? 'font-semibold text-Mblack' : 'text-Mgray'}`}>Monthly</span>
                            <button
                                onClick={() => setBillingPeriod(billingPeriod === 'yearly' ? 'monthly' : 'yearly')}
                                className={`relative w-14 h-7 rounded-full transition-colors ${billingPeriod === 'yearly' ? 'bg-Myellow' : 'bg-gray-300'}`}
                            >
                                <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${billingPeriod === 'yearly' ? 'right-1' : 'left-1'}`}></span>
                            </button>
                            <span className={`text-body-md ${billingPeriod === 'yearly' ? 'font-semibold text-Mblack' : 'text-Mgray'}`}>
                                Yearly <span className="text-green-600 text-sm">(Save 58%)</span>
                            </span>
                        </div>
                    </div>

                    <Row className='justify-content-center gap-y-4'>
                        {plans.map((plan) => {
                            const planPrice = SUBSCRIPTION_PLANS[plan.tier][billingPeriod].amount
                            const isCurrentPlan = currentSubscription?.plan_tier === plan.tier

                            return (
                                <Col key={plan.tier} lg={4} md={6}>
                                    <div className={`bg-white rounded-[20px] p-6 h-full relative ${
                                        plan.popular ? 'border-2 border-[#FEC51C] shadow-lg' : ''
                                    }`}>
                                        {plan.popular && (
                                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FEC51C] text-Mblack px-4 py-1 rounded-full text__12 font-medium">
                                                Most Popular
                                            </div>
                                        )}

                                        <div className="text-center mb-6">
                                            <h4 className='text-h4 text-Mblack mb-2'>{plan.name}</h4>
                                            <p className='text-body-sm text-gray-500 mb-4'>{plan.description}</p>
                                            <div className="mb-4">
                                                <span className='text-h2 text-Mblack font-bold'>£{planPrice}</span>
                                                <span className='text-body-md text-gray-500'>/{billingPeriod === 'monthly' ? 'month' : 'year'}</span>
                                            </div>
                                        </div>

                                        <ul className='space-y-3 mb-6'>
                                            {plan.features.map((feature, index) => (
                                                <li key={index} className='flex items-start gap-2'>
                                                    <svg className='w-5 h-5 text-[#FEC51C] mt-0.5 flex-shrink-0' fill='currentColor' viewBox='0 0 20 20'>
                                                        <path fillRule='evenodd' d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z' clipRule='evenodd' />
                                                    </svg>
                                                    <span className='text-body-md text-gray-700'>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>

                                        {isCurrentPlan ? (
                                            <div className="text-center py-3 bg-gray-100 rounded-[12px] font-medium text__14 text-gray-700">
                                                Current Plan
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handleSubscribe(plan.tier)}
                                                disabled={loading}
                                                className={`w-full ${plan.popular ? 'btn-primary' : 'btn-secondary'}`}
                                            >
                                                {loading ? 'Loading...' : 'Subscribe Now'}
                                            </button>
                                        )}
                                    </div>
                                </Col>
                            )
                        })}
                    </Row>

                    {currentSubscription && (
                        <div className="mt-8 text-center">
                            <button
                                onClick={handleManageSubscription}
                                disabled={loading}
                                className='btn-secondary'
                            >
                                {loading ? 'Loading...' : 'Manage Subscription'}
                            </button>
                        </div>
                    )}

                    <div className="mt-8 p-6 bg-white rounded-[20px]">
                        <h5 className='text-h6 text-Mblack mb-3'>💰 Keep 100% of Your Sales</h5>
                        <p className='text-body-md text-gray-600 mb-4'>
                            Unlike other platforms, Kinn doesn't take a commission on your sales. You pay a simple monthly or yearly subscription, and keep all your revenue (minus standard Stripe payment processing fees of 1.5% + 20p).
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 bg-Koffwhite rounded-[12px]">
                                <h6 className='font-medium text__14 text-Mblack mb-2'>✓ No Commission</h6>
                                <p className='text__12 text-gray-600'>Keep 100% of your sales revenue</p>
                            </div>
                            <div className="p-4 bg-Koffwhite rounded-[12px]">
                                <h6 className='font-medium text__14 text-Mblack mb-2'>✓ Simple Pricing</h6>
                                <p className='text__12 text-gray-600'>Just your subscription + Stripe fees</p>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>
        </Fragment>
    )
}

export default Subscription